import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Shield, Zap, Heart, TrendingUp, Star, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  const stats = [
    { value: '2025', label: 'Founded in London' },
    { value: 'Hosts', label: 'Built by actual hosts' },
    { value: '1', label: 'Platform for everything' },
    { value: '∞', label: 'Room to grow' },
  ];

  const values = [
    {
      icon: <Shield size={28} className="text-white" />,
      title: 'Trust First',
      description: 'Real identity and email verification, payments handled by Stripe, and clear standards for every listing. We believe trust is the foundation of every great stay.',
    },
    {
      icon: <Target size={28} className="text-white" />,
      title: 'Host Success',
      description: "We exist to help hosts grow. From smart pricing tools to our deal marketplace, everything we build puts host profitability first.",
    },
    {
      icon: <Heart size={28} className="text-white" />,
      title: 'Exceptional Experiences',
      description: "Great stays don't happen by accident. We give hosts the tools and support to deliver consistently outstanding guest experiences.",
    },
    {
      icon: <Zap size={28} className="text-white" />,
      title: 'Intelligent Technology',
      description: 'Powered by real data, our platform delivers insights that help hosts make smarter decisions and maximise returns.',
    },
  ];


  const milestones = [
    { year: 'Late 2025', event: 'We started out as hosts in London — excited about short-term rentals and the potential to build something meaningful.' },
    { year: 'The reality', event: 'We quickly discovered how overwhelming the space actually is. The market felt fragmented, intimidating, and full of unnecessary complexity that discouraged anyone new from giving it a go.' },
    { year: 'The question', event: 'We kept asking ourselves: why is this so hard? Booking guests, finding landlords, managing operations, tracking money — every piece lived in a different place, or didn\'t exist at all.' },
    { year: 'The idea', event: 'What if there was one platform that pulled it all together — a smarter, simpler way to run a short-term rental business without needing to be an expert first?' },
    { year: 'We built it', event: 'So we stopped waiting for someone else to do it and built Ariv ourselves — the platform we wished had existed when we started.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Browse</Link>
            <Link to="/become-host" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Become a Host</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-[#1F3A68] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }} />
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
              <Star size={14} className="text-yellow-400" />
              Our Story
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Built by hosts,<br />
              <span style={{ color: '#E5A54B' }}>because hosting is hard.</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl">
              We started out as hosts ourselves in London. The space was exciting — but overwhelming. Everything was scattered, complicated, or just missing entirely. So we built the platform we wished had existed.
            </p>
            <p className="text-sm text-white/60 mt-4 font-medium">
              Ariv is created by Brick and Capital Ltd
            </p>
            <p className="text-sm text-white/50 mt-1">
              Bowness House, 815 Old Kent Rd, London SE15 1NX
            </p>
            <p className="text-sm text-white/50 mt-1">
              <a href="mailto:hello@ariv.co.uk" className="hover:text-white/70 underline">hello@ariv.co.uk</a>
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-[#1F3A68] mb-1">{stat.value}</div>
                <div className="text-gray-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[#CB3B3B] font-semibold text-sm mb-4">
              <Target size={16} />
              OUR MISSION
            </div>
            <h2 className="text-4xl font-bold text-[#1F3A68] mb-6 leading-tight">
              We tried hosting. It was a lot.
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              When we started hosting in London in late 2025, we expected it to be challenging — but not like this. The short-term rental world is carved up into a dozen disconnected pieces: finding the right properties, negotiating with landlords, managing guests, tracking income, handling services. None of it talks to each other.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              The complexity is genuinely discouraging. We met plenty of people who had the ambition to get started but gave up before they ever did because it all felt too tedious and fragmented. We felt the same way — and then decided to do something about it.
            </p>
            <Link
              to="/become-host"
              className="inline-flex items-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1F3A68]/90 transition-colors"
            >
              Start your host journey
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/70 rounded-3xl p-10 text-white">
              <h3 className="text-2xl font-bold mb-8">What we believe</h3>
              <div className="space-y-5">
                {[
                  'Getting started should feel possible, not overwhelming',
                  'Everything a host needs should live in one place',
                  'Landlords and hosts deserve a direct, transparent connection',
                  'Complexity kills good ideas before they start',
                  'If we felt the frustration, so did thousands of others',
                ].map((belief, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white/90">{belief}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-[#CB3B3B] font-semibold text-sm mb-3">
              <Heart size={16} />
              OUR VALUES
            </div>
            <h2 className="text-4xl font-bold text-[#1F3A68]">What drives us every day</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-[#1F3A68] flex items-center justify-center mb-5">
                  {v.icon}
                </div>
                <h3 className="text-lg font-bold text-[#1F3A68] mb-3">{v.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story / Timeline */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 text-[#CB3B3B] font-semibold text-sm mb-3">
            <TrendingUp size={16} />
            OUR JOURNEY
          </div>
          <h2 className="text-4xl font-bold text-[#1F3A68]">How it started</h2>
        </div>
        <div className="relative">
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 transform md:-translate-x-0.5" />
          <div className="space-y-8">
            {milestones.map((m, i) => (
              <div key={i} className={`relative flex items-start gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-16 md:pl-0`}>
                  <div className={`bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow inline-block text-left`}>
                    <div className="text-[#CB3B3B] font-bold text-lg mb-2">{m.year}</div>
                    <p className="text-gray-700">{m.event}</p>
                  </div>
                </div>
                <div className="absolute left-6 md:relative md:left-0 flex-shrink-0 w-5 h-5 rounded-full bg-[#1F3A68] border-4 border-white shadow-md md:mt-6 mt-6" />
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we built */}
      <section className="bg-[#1F3A68] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-white/60 font-semibold text-sm mb-3">
              <Zap size={16} />
              WHAT WE BUILT
            </div>
            <h2 className="text-4xl font-bold text-white">One platform. Everything you need.</h2>
            <p className="text-white/70 mt-3 text-lg max-w-2xl mx-auto">
              We took every painful, fragmented part of running a short-term rental and brought it into one place.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { emoji: '🏠', title: 'Property listings', desc: 'List and manage your properties with no friction.' },
              { emoji: '🤝', title: 'Deal Marketplace', desc: 'Connect directly with landlords for R2R agreements — no middlemen.' },
              { emoji: '📅', title: 'Booking management', desc: 'Accept, track, and manage bookings in one dashboard.' },
              { emoji: '💬', title: 'Integrated messaging', desc: 'Talk to guests, landlords, and service providers all in one inbox.' },
              { emoji: '🛎️', title: 'Guest services', desc: 'Food delivery, rides, cleaning — upsells that improve stays and earn you more.' },
              { emoji: '📊', title: 'Analytics & insights', desc: 'See what\'s working, what\'s not, and where to grow next.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 rounded-2xl p-6 hover:bg-white/15 transition-colors">
                <div className="text-3xl mb-3">{item.emoji}</div>
                <h3 className="text-white font-bold mb-1.5">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For anyone thinking about it */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="bg-[#1F3A68]/5 border border-[#1F3A68]/10 rounded-3xl p-12">
          <div className="flex items-start gap-6">
            <div className="text-5xl flex-shrink-0">💡</div>
            <div>
              <h2 className="text-3xl font-bold text-[#1F3A68] mb-4">If you've been thinking about it but haven't started — this is for you.</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-4">
                The short-term rental business is genuinely a good one. But the barrier to getting in has always been the chaos of it — not knowing where to start, who to talk to, how to find the right property, how to manage everything once you do.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We built Ariv to lower that barrier. To make the thing that felt overwhelming feel manageable. You don't need to be an expert. You just need a place to start — and we've tried to make this that place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#1F3A68] to-[#2D5AA0] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Give it a go.</h2>
          <p className="text-white/80 text-xl mb-10">We built this because we wished it existed. If you've been on the fence about short-term rentals, this is the easier way in.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/become-host"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1F3A68] px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition-colors"
            >
              Become a Host
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/50 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/help" className="hover:text-white">Help Center</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv</p>
          <p className="text-white/40 text-xs mt-2"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
